import { get } from 'lodash';

import REGIONS from '../../../../constants/region-type';
import FARES from '../../../../constants/fare-types';

function product({ ancillary, emd, flightItineraryType, legCode, legCollection, quantity, seat, segmentCode, marketingCarrier, brand, seatSelectionFee }) {
  const isRoundTrip = flightItineraryType && flightItineraryType === 'ROUNDTRIP';
  const flightType = isRoundTrip ? 'RT' : flightItineraryType === 'ONEWAY' ? 'OW' : 'MC';
  let route =  getRoute(legCode, legCollection);
  route = seat && segmentCode ? _getRouteBySeat(segmentCode, legCollection) : route;
  const fareFamily = getFare(legCode, legCollection);
  const marketingFlight = getMarketingFlight({ flightType, isRoundTrip, legCode, legCollection });

  return _getProduct({
    ancillary,
    emd,
    fareFamily,
    marketingCarrier,
    marketingFlight,
    quantity,
    route,
    seat,
    brand,
    seatSelectionFee,
    legCollection,
  });
}

function buildProductSchema({
  brand,
  category,
  id,
  name,
  position,
  price,
  pricepoints,
  baggageCheckedBag,
  baggageCarryonBag,
  variant = 'NA',
}) {
  return {
    name: name,
    id: id,
    price: price,
    pricepoints,
    brand: brand,
    category: category,
    [baggageCheckedBag === 'NA' ? 'baggageCarryonBag' : 'baggageCheckedBag']: baggageCarryonBag,
    variant: variant,
    position: position,
  };
}

function getFare(legCode, legCollection = []) {
  let fareFamily = get(legCollection, '[0].segmentsCollection[0].fareFamily', 'NA');
  if (legCode) {
    legCollection.forEach((leg) => {
      const segments = leg.segmentsCollection;
      const totalSgmentsByLeg = segments.length;
      if (leg.legCode === legCode) {
        fareFamily = totalSgmentsByLeg && segments[0].fareFamily ? segments[0].fareFamily : 'NA';
      }
    });
  }
  fareFamily = fareFamily || 'NA';
  return fareFamily;
}

function getRoute(legCode, legCollection = []) {
  let departureAirport = get(legCollection, '[0].segmentsCollection[0].departureAirport', 'NA');
  let arrivalAirport = get(legCollection, '[0].segmentsCollection[0].arrivalAirport', 'NA');
  if (departureAirport !== 'NA') {
    const lengthSegment = legCollection[0].segmentsCollection.length;
    arrivalAirport = legCollection[0].segmentsCollection[(lengthSegment - 1)].arrivalAirport;
  }

  if (legCode) {
    legCollection.forEach((leg) => {
      if (leg.legCode === legCode) {
        const totalSegments = leg.segmentsCollection.length;
        if (totalSegments) {
          departureAirport = get(leg, 'segmentsCollection[0].departureAirport', 'NA');
          arrivalAirport = get(leg, `segmentsCollection[${(totalSegments - 1)}].arrivalAirport`, 'NA');
        }
      }
    });
  }
  return `${departureAirport}-${arrivalAirport}`;
}

function _getRouteBySeat(segmentCode, legCollection = []) {
  let route = 'NA';
  legCollection.forEach((leg) => {
    leg.segmentsCollection.forEach((segment) => {
      if (segment.segmentCode === segmentCode) {
        route = `${segment.departureAirport}-${segment.arrivalAirport}`;
      }
    });
  });
  return route;
}

function getMarketingFlight({ flightType, isRoundTrip, legCode, legCollection = [] }) {
  let marketingFlight = '';
  let arrivalSegment = '';

  legCollection.forEach((leg, index) => {
    if (leg.legCode === legCode) {
      marketingFlight = REGIONS.REGIONS_OF_FLIGHT[leg.marketFlight];
      arrivalSegment = !isRoundTrip ? '' : index === 0 ? '-IDA' : '-VUELTA';
    }

    if ( marketingFlight === 'Transborder' ) {
      marketingFlight += leg.regionKey && leg.regionKey === 'USA' ? '-US' : '-CA';
    }
  });

  return `${flightType}${arrivalSegment}-${marketingFlight}`;
}

function _getProduct({ ancillary, emd, fareFamily, marketingCarrier, marketingFlight, quantity, route, seat, brand, seatSelectionFee, legCollection }) {
  let product = {};
  if (seat) {
    product = {
      name: _getSeatName(seat),
      id: seat.code,
      price: seat.currency ? seat.getTotal() : seatSelectionFee ? seatSelectionFee.total : 0,
      brand: brand,
      category: `Misc/Ancillary/${route}/${FARES.FARE_FAMILIES_NAMES[fareFamily]}/${marketingFlight}`,
      variant: emd ? emd : 'NA',
      quantity: quantity ? quantity : 1,
    };
  } else if (ancillary.type === 'UPG' && ancillary.ancillaryOffer) {
    let route = route;
    let id = ancillary.type;
    const legCollectionSelect = legCollection.filter(item => item.segmentsCollection.find(segment => segment.segmentCode === ancillary.segmentCode));
    const segmentSelect = legCollectionSelect[0].segmentsCollection.find(segment => segment.segmentCode === ancillary.segmentCode);

    route = segmentSelect ? `${segmentSelect.departureAirport}-${segmentSelect.arrivalAirport}` : route;
    const aircraftType = segmentSelect && segmentSelect.aircraftType;
    const fareFamilyPerSegment = segmentSelect ? segmentSelect.fareFamily : fareFamily;

    if ( aircraftType == 787 || aircraftType == 783 || aircraftType == 788 || aircraftType == 789 || aircraftType === '89S') {
      if ( segmentSelect && (segmentSelect.arrivalAirport === 'JFK' || segmentSelect.arrivalAirport === 'LAX') ) {
        id = 'U87';
      }
    }

    product = {
      name: ancillary.ancillaryOffer.ancillary.commercialName,
      id: id,
      price: ancillary.ancillaryOffer.ancillary.getTotal() || 0,
      brand: 'Aeromexico',
      category: `Upgrade/Ancillary/${route}/${FARES.FARE_FAMILIES_NAMES[fareFamilyPerSegment]}/${marketingFlight}`,
      variant: 'NA',
      quantity: quantity,
    };
  } else {
    product = {
      name: ancillary.commercialName,
      id: ancillary.type,
      price: ancillary.getTotal() || 0,
      brand: marketingCarrier,
      category: `${ancillary.type === 'UPG' || ancillary.type === 'U87' ? 'Upgrade' : 'Misc'}/Ancillary/${route}/${FARES.FARE_FAMILIES_NAMES[fareFamily]}/${marketingFlight}`,
      variant: emd ? [emd] : 'NA',
      quantity: quantity,
    };
  }

  return product;
}

function _getSeatName(seatChoice) {
  let seatName = 'Seat Choice';
  if (seatChoice.type) {
    switch (seatChoice.type) {
      case 'EXIT_ROW_UPGRADE':
        seatName = 'Emergency Exit Seat';
        break;
      case 'PREFERRED_UPGRADE':
        seatName = 'Preferred Seat';
        break;
      case 'AMPLUS_UPGRADE':
        seatName = 'AMPlus Seat';
        break;
      /* cases for Delta */
      case 'Exit':
        seatName = 'Emergency Exit Seat';
        break;
      case 'Preferred':
        seatName = 'Preferred Seat';
        break;
    }
  }
  return seatName;
}

export default {
  getFare,
  getRoute,
  getMarketingFlight,
  buildProductSchema,
  product,
};
